import React from 'react';
import PropTypes from 'prop-types';

const TONE = {
  critical: { line: '#d92b2b', area: 'rgba(217,43,43,0.08)', dot: '#d92b2b' },
  warning: { line: '#e8890c', area: 'rgba(232,137,12,0.08)', dot: '#e8890c' },
  neutral: { line: '#6b6e66', area: 'rgba(107,110,102,0.07)', dot: '#6b6e66' }
};

const W = 400;

/**
 * Lightweight inline SVG trend sparkline (area + line + endpoint dot, with
 * optional dashed threshold lines). Used in the Assets detail pane's
 * contextual trend. Stretched to container width (preserveAspectRatio none).
 */
const Sparkline = ({ data, thresholds, tone, height, domain }) => {
  const values = (data || [])
    .filter(v => v != null && !Number.isNaN(Number(v)))
    .map(Number);
  if (values.length < 2) return null;

  const tn = TONE[tone] || TONE.neutral;

  let min;
  let max;
  if (domain) {
    [min, max] = domain;
  } else {
    min = Math.min(...values);
    max = Math.max(...values);
    const pad = (max - min) * 0.12 || 1;
    min -= pad;
    max += pad;
  }
  const span = max - min || 1;
  const yOf = v => height - ((v - min) / span) * height;
  const xOf = i => (i / (values.length - 1)) * W;

  const pts = values.map((v, i) => `${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`);
  const linePath = `M${pts.join(' L')}`;
  const areaPath = `${linePath} L${W},${height} L0,${height}Z`;
  const lastY = yOf(values[values.length - 1]).toFixed(1);

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none">
      {(thresholds || []).map((th) => {
        const y = yOf(th.value);
        if (y < 0 || y > height) return null;
        return (
          <line
            key={th.value}
            x1="0"
            y1={y}
            x2={W}
            y2={y}
            stroke={th.color}
            strokeWidth="1"
            strokeDasharray="4 3"
            opacity="0.4"
          />
        );
      })}
      <path d={areaPath} fill={tn.area} />
      <path d={linePath} fill="none" stroke={tn.line} strokeWidth="1.8" />
      <circle cx={W} cy={lastY} r="3.5" fill={tn.dot} />
    </svg>
  );
};

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  thresholds: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    color: PropTypes.string
  })),
  tone: PropTypes.oneOf(['critical', 'warning', 'neutral']),
  height: PropTypes.number,
  domain: PropTypes.arrayOf(PropTypes.number)
};

Sparkline.defaultProps = {
  data: [],
  thresholds: [],
  tone: 'neutral',
  height: 54,
  domain: null
};

export default Sparkline;
